import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, Shield, ClipboardList, BarChart3,
  LogOut, Menu, X, Heart, Layers, FileCheck, Bell, ScrollText, Umbrella, UserPlus, KeyRound, UserCheck, Building2,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getNavItems } from '../utils/roleAccess';
import { notificationsAPI } from '../services/api';

const ICONS = {
  '/': LayoutDashboard,
  '/my-family': UserPlus,
  '/policies': Shield,
  '/slabs': Layers,
  '/employees': Users,
  '/employee-portal-users': KeyRound,
  '/approvals': UserCheck,
  '/hospital-management': Building2,
  '/hospitals': Building2,
  '/term-coverages': Umbrella,
  '/claims': ClipboardList,
  '/term-claims': FileCheck,
  '/endorsements': FileCheck,
  '/reports': BarChart3,
  '/notifications': Bell,
  '/audit': ScrollText,
};

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  const navItems = getNavItems(user?.role);

  useEffect(() => {
    notificationsAPI.unreadCount()
      .then(({ data }) => setUnread(data.count))
      .catch(() => {});
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const NavContent = () => (
    <>
      <div className="flex items-center gap-3 px-4 py-6 border-b border-slate-700">
        <div className="bg-primary-500 p-2 rounded-lg"><Heart className="w-6 h-6 text-white" /></div>
        <div>
          <h1 className="text-white font-bold text-lg">EMIMS</h1>
          <p className="text-slate-400 text-xs">Insurance Management</p>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1">
        {navItems.map(({ to, label }) => {
          const Icon = ICONS[to] || LayoutDashboard;
          return (
            <NavLink key={to} to={to} end={to === '/'} onClick={() => setSidebarOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${isActive ? 'bg-primary-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`}>
              <Icon className="w-5 h-5" />{label}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-4 border-t border-slate-700">
        <div className="text-slate-300 text-sm truncate">{user?.fullName}</div>
        <div className="text-slate-500 text-xs mb-3 capitalize">{user?.role?.replace(/_/g, ' ')}</div>
        <button onClick={handleLogout} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm w-full">
          <LogOut className="w-4 h-4" />Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex">
      <aside className="hidden lg:flex lg:flex-col lg:w-64 bg-slate-800 fixed inset-y-0 left-0 z-30"><NavContent /></aside>
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-slate-800 flex flex-col">
            <button onClick={() => setSidebarOpen(false)} className="absolute top-4 right-4 text-slate-400"><X className="w-6 h-6" /></button>
            <NavContent />
          </aside>
        </div>
      )}
      <div className="flex-1 lg:ml-64">
        <header className="bg-white border-b sticky top-0 z-20">
          <div className="flex items-center justify-between px-4 sm:px-6 py-4">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden"><Menu className="w-6 h-6" /></button>
            <h2 className="text-lg font-semibold hidden sm:block">Employee Insurance Management System</h2>
            <div className="flex items-center gap-3">
              {unread > 0 && (
                <Link to="/notifications" className="flex items-center gap-1 text-sm text-slate-600 hover:text-primary-600">
                  <Bell className="w-4 h-4" />{unread}
                </Link>
              )}
              <Link to="/" className="text-primary-600 font-semibold lg:hidden">EMIMS</Link>
            </div>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto"><Outlet /></main>
      </div>
    </div>
  );
}
